import { useLayoutEffect, type RefObject } from 'react';

export type Placement = 'bottom' | 'top' | 'right' | 'left';
export type Align = 'start' | 'end' | 'center';

export interface AnchoredPositionOptions {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  floatingRef: RefObject<HTMLElement | null>;
  placement?: Placement;
  align?: Align;
  /** Gap between anchor and floating element, in px. */
  offset?: number;
  /** Match the floating element's min-width to the anchor width. */
  matchWidth?: boolean;
}

const VIEWPORT_MARGIN = 8;

/**
 * Positions a `position: fixed` floating element next to its anchor. Flips to the other side
 * when there is not enough room and clamps inside the viewport. Styles are written directly to
 * the element (no re-render needed), and updated on scroll/resize.
 */
export function useAnchoredPosition({
  open,
  anchorRef,
  floatingRef,
  placement = 'bottom',
  align = 'start',
  offset = 6,
  matchWidth = false,
}: AnchoredPositionOptions): void {
  useLayoutEffect(() => {
    if (!open) return;
    const anchor = anchorRef.current;
    const floating = floatingRef.current;
    if (!anchor || !floating) return;

    function update() {
      if (!anchor || !floating) return;
      const a = anchor.getBoundingClientRect();
      if (matchWidth) floating.style.minWidth = `${Math.round(a.width)}px`;
      const f = floating.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      let side: Placement = placement;
      let top: number;
      let left: number;
      let available: number;

      if (placement === 'right' || placement === 'left') {
        const spaceRight = vw - a.right - offset - VIEWPORT_MARGIN;
        const spaceLeft = a.left - offset - VIEWPORT_MARGIN;
        if (side === 'right' && f.width > spaceRight && spaceLeft > spaceRight) side = 'left';
        else if (side === 'left' && f.width > spaceLeft && spaceRight > spaceLeft) side = 'right';
        left = side === 'right' ? a.right + offset : a.left - offset - f.width;
        top = a.top + a.height / 2 - f.height / 2;
        available = vh - 2 * VIEWPORT_MARGIN;
      } else {
        const spaceBelow = vh - a.bottom - offset - VIEWPORT_MARGIN;
        const spaceAbove = a.top - offset - VIEWPORT_MARGIN;
        if (side === 'bottom' && f.height > spaceBelow && spaceAbove > spaceBelow) side = 'top';
        else if (side === 'top' && f.height > spaceAbove && spaceBelow > spaceAbove) {
          side = 'bottom';
        }
        top = side === 'bottom' ? a.bottom + offset : a.top - offset - f.height;
        if (align === 'end') left = a.right - f.width;
        else if (align === 'center') left = a.left + a.width / 2 - f.width / 2;
        else left = a.left;
        available = side === 'bottom' ? spaceBelow : spaceAbove;
      }

      top = Math.max(VIEWPORT_MARGIN, Math.min(top, vh - f.height - VIEWPORT_MARGIN));
      left = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - f.width - VIEWPORT_MARGIN));

      floating.style.position = 'fixed';
      floating.style.top = `${Math.round(top)}px`;
      floating.style.left = `${Math.round(left)}px`;
      floating.style.maxHeight = `${Math.max(160, Math.round(available))}px`;
      floating.dataset.side = side;
    }

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    const observer =
      typeof ResizeObserver === 'function' ? new ResizeObserver(() => update()) : undefined;
    observer?.observe(floating);
    observer?.observe(anchor);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      observer?.disconnect();
    };
  }, [open, anchorRef, floatingRef, placement, align, offset, matchWidth]);
}
